import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CountriesService } from '@core/services/countries.service';
import { AllowedLocation } from '@core/typings/program.typing';
import { ALL_SKIP_FILTER } from '@yourcause/common';
import { TypeaheadSelectOption } from '@yourcause/common/core-forms';
import { isEqual } from 'lodash';

@Component({
    selector: 'gc-location-filtering',
    templateUrl: './location-filtering.component.html',
    styleUrls: ['./location-filtering.component.scss'],
    standalone: false
})
export class LocationFilteringComponent implements OnInit {
  @Input() allowedLocations: AllowedLocation[];
  @Input() allowManage: boolean;
  @Output() onChange = new EventEmitter<AllowedLocation[]>();
  
  hasEmptyCountrySelect = false;
  selectedLocations: AllowedLocation[] = [];
  countryOptionsMap: Record<number, TypeaheadSelectOption[]> = {};
  countryIdToRegionOptionsMap = this.countriesService.countryIdToRegionOptionsMap;
  countryIdToNameMap = this.countriesService.countryIdToNameMap;

  constructor (
    private countriesService: CountriesService
  ) { }

  ngOnInit () {
    const adaptedLocations = this.allowedLocations.map<AllowedLocation>((location) => {
      return {
        countryId: location.countryId ?
          location.countryId :
          ALL_SKIP_FILTER as any,
        regionIds: location.regionIds.length > 0 ?
          location.regionIds :
          [ALL_SKIP_FILTER as any]
      };
    });
    this.setSelectedLocations(adaptedLocations, true);
  }

  setCountryOptionsMap () {
    this.countryOptionsMap = this.selectedLocations.reduce((acc, selectedLocation, index) => ({
      ...acc,
      [index]: this.countriesService.countriesWithRegions.filter((country) => {
        const isSelected = country.countryId === selectedLocation.countryId;
        const selectedOnAnotherRow = this.selectedLocations.some(_location => {
          return _location.countryId === country.countryId;
        });

        return isSelected || !selectedOnAnotherRow;
      }).map<TypeaheadSelectOption>((item) => {
        return {
          value: item.countryId,
          label: item.countryName
        };
      })
    }), {});
    this.hasEmptyCountrySelect = this.selectedLocations.some((location) => {
      return !location.countryId;
    });
  }

  setSelectedLocations (
    locations: AllowedLocation[],
    isInit = false
  ) {
    this.selectedLocations = locations;
    this.setCountryOptionsMap();
    const isValid = locations.every((location) => {
      return !!location.countryId
    });
    if (isValid && !isInit) {
      const locationsToEmit = this.selectedLocations.filter((location) => {
        return location.countryId !== (ALL_SKIP_FILTER as any);
      }).map<AllowedLocation>((location) => {
        let regionIds: number[] = [];
        if (!location.regionIds.includes(ALL_SKIP_FILTER as any)) {
          regionIds = [...location.regionIds];
        }

        return {
          countryId: location.countryId,
          regionIds
        };
      });
      if (!isEqual(locationsToEmit, this.allowedLocations)) {
        this.onChange.emit(locationsToEmit);
      }
    }
  }

  handleCountryChange (index: number) {
    // If the country changes, we need to reset the regions
    const selectedLocations = [
      ...this.selectedLocations.slice(0, index),
      {
        ...this.selectedLocations[index],
        regionIds: [ALL_SKIP_FILTER as any]
      },
      ...this.selectedLocations.slice(index + 1)
    ];
    this.setSelectedLocations(selectedLocations);
  }

  regionAdded (
    regionId: number|string,
    index: number
  ) {
    const location = this.selectedLocations[index];
    // If they select all regions, remove other options selected and vice versa
    const addingAllRegions = regionId === ALL_SKIP_FILTER;
    const selectedLocations = [
      ...this.selectedLocations.slice(0, index),
      {
        ...this.selectedLocations[index],
        regionIds: location.regionIds.filter((region) => {
          if (addingAllRegions) {
            return region === (ALL_SKIP_FILTER as any);
          } else {
            return region !== ALL_SKIP_FILTER as any;
          }
        })
      },
      ...this.selectedLocations.slice(index + 1)
    ];
    this.setSelectedLocations(selectedLocations);
  }

  regionRemoved (
    regionId: number|string,
    index: number
  ) {
    let selectedLocations = this.selectedLocations;
    const location = this.selectedLocations[index];
    const removingAllRegions = location.regionIds.length === 0;
    // If they remove all selections, add the "All regions" option back
    if (removingAllRegions) {
      selectedLocations = [
        ...this.selectedLocations.slice(0, index),
        {
          ...this.selectedLocations[index],
          regionIds: [ALL_SKIP_FILTER as any]
        },
        ...this.selectedLocations.slice(index + 1)
      ];
    }
    this.setSelectedLocations(selectedLocations);
  }

  getEmptyLocation (): AllowedLocation {
    return {
      countryId: null,
      regionIds: [ALL_SKIP_FILTER as any]
    };
  }

  manageLocations () {
    const selectedLocations = [this.getEmptyLocation()];
    this.setSelectedLocations(selectedLocations);
  }

  addCountry () {
    const selectedLocations = [
      ...this.selectedLocations,
      this.getEmptyLocation()
    ];
    this.setSelectedLocations(selectedLocations);
  }

  removeCountry (index: number) {
    const selectedLocations = [
      ...this.selectedLocations.slice(0, index),
      ...this.selectedLocations.slice(index + 1)
    ];
    this.setSelectedLocations(selectedLocations);
  }
}
